import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { isOwnerEmail, normalizeEmail, PRIMARY_OWNER_EMAIL } from '../firebase/admin';
import { motion } from 'motion/react';
import { Lock, Mail, ArrowRight, ShieldCheck, AlertCircle, Chrome } from 'lucide-react';
import Seo from '../components/Seo';
import { PAGE_METADATA } from '../seo/pageMetadata';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading } = useAuth();

  React.useEffect(() => {
    if (!authLoading && user && isAdmin) {
      navigate('/admin');
    }
  }, [user, isAdmin, authLoading, navigate]);

      const handleAdminCheck = async (user: any) => {
        try {
          const isPrimaryOwner = isOwnerEmail(user.email);

          if (isPrimaryOwner) {
            const userDocRef = doc(db, 'users', user.uid);
            await setDoc(
              userDocRef,
              {
                email: normalizeEmail(user.email),
                displayName: user.displayName ?? null,
                role: 'admin',
                accessLevel: 'owner',
                isOwner: true,
                updatedAt: serverTimestamp(),
                createdAt: serverTimestamp()
              },
              { merge: true }
            );
            navigate('/admin');
          } else {
            setError(`Access denied. Only the project owner account (${PRIMARY_OWNER_EMAIL}) can access this admin area.`);
            await auth.signOut();
          }
        } catch (err: any) {
          console.error("Admin check error:", err);
          if (isOwnerEmail(user.email)) {
            navigate('/admin');
          } else {
            setError('Failed to verify admin privileges. Please try again.');
            await auth.signOut();
          }
        }
      };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, normalizeEmail(email), password);
      await handleAdminCheck(userCredential.user);
    } catch (err: any) {
      console.error("Login error:", err);
      if (err.code === 'permission-denied' || err.message?.includes('permissions')) {
        setError(`Permission denied for ${email}. Sign in with the owner account ${PRIMARY_OWNER_EMAIL}.`);
      } else if (err.code === 'auth/operation-not-allowed') {
        setError('Email/Password sign-in is not enabled in Firebase Authentication. Enable it in the Firebase Console or use Google sign-in.');
      } else if (err.code === 'auth/user-not-found') {
        setError(`No Firebase Authentication user exists for ${normalizeEmail(email)}. Creating a project owner in Firebase Console does not create an app login automatically.`);
      } else if (err.code === 'auth/invalid-credential') {
        setError('Invalid credentials. If this is the new owner account, make sure the Firebase Authentication user exists and the password is correct.');
      } else {
        setError(err.message || 'Failed to login. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      await handleAdminCheck(result.user);
    } catch (err: any) {
      console.error("Google login error:", err);
      setError(err.message || 'Failed to login with Google.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo {...PAGE_METADATA.adminLogin} noIndex />
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center bg-slate-50 px-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100"
      >
        <div className="p-12">
          <div className="text-center mb-10">
            <div className="w-20 h-20 bg-royal-accent rounded-3xl flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-10 h-10 text-royal-purple" />
            </div>
            <h1 className="text-3xl font-display font-black text-royal-dark tracking-tighter">Admin Portal</h1>
            <p className="text-slate-500 mt-2 font-medium">Secure access for Delight Business Empowerment administrators</p>
          </div>

          {error && (
            <div className="mb-8 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-center gap-3 text-red-600 text-sm font-medium">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <label className="block text-xs font-black text-royal-dark uppercase tracking-[0.2em] ml-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input 
                  type="email" 
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@delightempowerment.com"
                  className="w-full pl-14 pr-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all font-medium"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-xs font-black text-royal-dark uppercase tracking-[0.2em] ml-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input 
                  type="password" 
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-14 pr-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-royal-purple focus:border-transparent outline-none transition-all font-medium"
                />
              </div>
            </div>

            <button 
              type="submit"
              disabled={loading}
              className="w-full bg-royal-dark text-white py-5 rounded-2xl font-black text-lg shadow-2xl hover:bg-royal-purple transition-all flex items-center justify-center gap-3 disabled:opacity-50"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white"></div>
              ) : (
                <>Login to Dashboard <ArrowRight className="w-5 h-5" /></>
              )}
            </button>
          </form>

          <div className="relative my-10">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-100"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-4 text-slate-400 font-black tracking-widest">Or continue with</span>
            </div>
          </div>

          <button 
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full bg-white border-2 border-slate-100 text-royal-dark py-5 rounded-2xl font-black text-lg hover:bg-slate-50 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
          >
            <Chrome className="w-6 h-6" />
            Sign in with Google
          </button>

          <div className="mt-10 text-center">
            <p className="text-xs text-slate-400 font-medium">
              Authorized access only. All activities are monitored.
            </p>
          </div>
        </div>
      </motion.div>
      </div>
    </>
  );
};

export default AdminLogin;
